// Addresses (Mitch, 2026-11-03: "Any address field should be properly formatted, in a way that is globally smart and
// intelligently splits up fields to the proper items for future use (City, State, Region, Postal Code, etc) … We want to
// ensure that addresses are proper"). One shape everywhere (`Address`: street lines, district, city, region, postal
// code, country as its ISO code), every country's own rules from Google's address data (address-data.ts, what Chrome's
// address forms use; nothing is looked up online): which fields it has and what it calls them ("State" and "ZIP code"
// in the US, "Province" and "District" in China, "Post town" and "Postcode" in the UK), which are required, its postal
// code's pattern, its states or provinces, and the order it's written in.
// - `addressFields(cc)`: the fields to show for a country, in its order, with labels, options and examples.
// - `normalizeAddress(a)`: tidied: spaces, the country's code, a state or province by its proper name ("pennsylvania" →
//   PA, "广东省" → Guangdong), a Chinese city by its Latin name, the postal code as the country writes it.
// - `addressProblems(a)`: what isn't right yet, in words ("A ZIP code looks like 95014").
// - `formatAddress(a)`: its lines as the country writes them, in Latin script (PO PDFs have no Chinese glyphs).
// - `parseAddress(text)`: a whole address pasted from an email or a PI split into its fields, Latin or Chinese script.
import { ADDRESS_DATA } from "./address-data.ts";

/** A country's rules as Google's address data has them (fields kept by scripts/address-data.ts). */
export type CountryRules = {
  name?: string;
  lang?: string;
  /** %N name, %O organization, %A street, %D district, %C city, %S state/province, %Z postal code, %n a new line. */
  fmt?: string;
  /** The same in Latin script, for countries that write theirs in another (China, Taiwan, Japan…). */
  lfmt?: string;
  /** Which of A, C, S, Z it needs. */
  require?: string;
  upper?: string;
  /** Its postal code, as a regular expression (no anchors). */
  zip?: string;
  zipex?: string;
  zip_name_type?: string;
  state_name_type?: string;
  locality_name_type?: string;
  sublocality_name_type?: string;
  sub_keys?: string;
  sub_names?: string;
  sub_lnames?: string;
  sub_isoids?: string;
  /** China: each province's cities (local names and Latin ones, ~-separated). */
  cities?: Record<string, { names: string; lnames: string }>;
};

export type Address = {
  line1: string;
  line2: string;
  /** A district or neighbourhood, where the country has one (China's 区). */
  district: string;
  city: string;
  /** A state, province or county: for a country with a list, its proper name (US/CA: the two letters). */
  region: string;
  postal_code: string;
  /** ISO 3166-1 alpha-2: US, CN, GB. */
  country: string;
};
export const ADDRESS_KEYS = ["line1", "line2", "district", "city", "region", "postal_code", "country"] as const;
export type AddressKey = (typeof ADDRESS_KEYS)[number];
export const EMPTY_ADDRESS: Address = { line1: "", line2: "", district: "", city: "", region: "", postal_code: "", country: "" };

const ZZ = ADDRESS_DATA.ZZ ?? {};
/** A country's rules, its own over the defaults (ZZ). */
export const rulesOf = (cc: string): CountryRules => ({ ...ZZ, ...(ADDRESS_DATA[cc.toUpperCase()] ?? {}) });
export const isCountry = (cc: string) => cc.toUpperCase() !== "ZZ" && !!ADDRESS_DATA[cc.toUpperCase()];

const SMALL = new Set(["and", "of", "the", "da", "du", "de", "la", "et"]);
const titleCase = (s: string) =>
  s
    .toLowerCase()
    .split(/(\s+|-|\(|\))/)
    .map((w, i) => (i > 0 && SMALL.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join("")
    .replace(/\bU\.s\./i, "U.S.");
/** A few countries read better by the name people use. */
const NAME_OF: Record<string, string> = {
  US: "United States",
  GB: "United Kingdom",
  KR: "South Korea",
  KP: "North Korea",
  TW: "Taiwan",
  HK: "Hong Kong",
  MO: "Macao",
  RU: "Russia",
  VN: "Vietnam",
  LA: "Laos",
  IR: "Iran",
  SY: "Syria",
  TZ: "Tanzania",
  BO: "Bolivia",
  VE: "Venezuela",
  MD: "Moldova",
  CZ: "Czechia",
};
export const countryName = (cc: string) => {
  const code = cc.toUpperCase();
  return NAME_OF[code] ?? (ADDRESS_DATA[code]?.name ? titleCase(ADDRESS_DATA[code].name as string) : code);
};
/** Every country, by name: for a picker. */
export const COUNTRIES: readonly { code: string; name: string }[] = Object.keys(ADDRESS_DATA)
  .filter((c) => c !== "ZZ")
  .map((code) => ({ code, name: countryName(code) }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Fields -----------------------------------------------------------------------------------------------------------

const STATE_LABELS: Record<string, string> = {
  area: "Area",
  county: "County",
  department: "Department",
  district: "District",
  do_si: "Province",
  emirate: "Emirate",
  island: "Island",
  oblast: "Oblast",
  parish: "Parish",
  prefecture: "Prefecture",
  province: "Province",
  state: "State",
};
const CITY_LABELS: Record<string, string> = { city: "City", district: "District", post_town: "Post town", suburb: "Suburb" };
const DISTRICT_LABELS: Record<string, string> = {
  district: "District",
  neighborhood: "Neighborhood",
  suburb: "Suburb",
  townland: "Townland",
  village_township: "Village / township",
};
const ZIP_LABELS: Record<string, string> = { zip: "ZIP code", postal: "Postal code", pin: "PIN code", eircode: "Eircode" };

export type FieldSpec = {
  key: Exclude<AddressKey, "country">;
  label: string;
  required: boolean;
  /** A state or province: its list (value = how it's stored). */
  options?: readonly { value: string; label: string }[];
  placeholder?: string;
};

/** The fields a country's address has, in the order it's written (Latin order), with its own labels. */
export function addressFields(cc: string): FieldSpec[] {
  const r = rulesOf(cc || "ZZ");
  const fmt = r.lfmt ?? r.fmt ?? "%A%n%C";
  const req = r.require ?? "AC";
  const out: FieldSpec[] = [];
  for (const m of fmt.matchAll(/%([ADCSZ])/g)) {
    const f = m[1];
    if (f === "A")
      out.push(
        { key: "line1", label: "Street address", required: req.includes("A") },
        { key: "line2", label: "Apt, suite, building (optional)", required: false },
      );
    if (f === "D") out.push({ key: "district", label: DISTRICT_LABELS[r.sublocality_name_type ?? "suburb"] ?? "District", required: false });
    if (f === "C") out.push({ key: "city", label: CITY_LABELS[r.locality_name_type ?? "city"] ?? "City", required: req.includes("C") });
    if (f === "S") {
      const subs = subdivisions(cc);
      out.push({
        key: "region",
        label: STATE_LABELS[r.state_name_type ?? "province"] ?? "Province",
        required: req.includes("S"),
        ...(subs.length ? { options: subs.map((s) => ({ value: s.value, label: s.label })) } : {}),
      });
    }
    if (f === "Z")
      out.push({
        key: "postal_code",
        label: ZIP_LABELS[r.zip_name_type ?? "postal"] ?? "Postal code",
        required: req.includes("Z"),
        placeholder: r.zipex?.split(",")[0],
      });
  }
  return out;
}

// States, provinces and Chinese cities ---------------------------------------------------------------------------

/** China's Latin names carry the kind of place: "Guangdong Sheng", "Shenzhen Shi", "Guangxi Zhuangzuzizhiqu". */
const latinClean = (s: string) => s.replace(/\s+(Sheng|Shi|Zizhiqu|Zhuangzuzizhiqu|Huizuzizhiqu|Weiwuerzizhiqu|Zizhizhou|Diqu|Meng|Xian|Qu)$/i, "").trim();
/** How a name is compared: no case, spaces, punctuation or words for the kind of place ("Province", "省", "City"). */
const keyOf = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKC")
    .replace(/(壮族|回族|维吾尔|自治区|特别行政区|省|市|province|state|sheng|shi|city|prefecture|county|zizhiqu)/g, "")
    .replace(/[\s.,'’\-()]/g, "");

type Subdivision = { key: string; name: string; latin: string; iso: string; value: string; label: string };
const subCache = new Map<string, Subdivision[]>();
/** A country's states or provinces: stored by `value` (the two letters for the US and Canada, else the Latin name). */
export function subdivisions(cc: string): Subdivision[] {
  const code = cc.toUpperCase();
  const hit = subCache.get(code);
  if (hit) return hit;
  const r = ADDRESS_DATA[code];
  const keys = r?.sub_keys ? r.sub_keys.split("~") : [];
  const names = r?.sub_names?.split("~") ?? [];
  const lnames = r?.sub_lnames?.split("~") ?? [];
  const isos = r?.sub_isoids?.split("~") ?? [];
  const byCode = /^[A-Z]{2,3}$/.test(keys[0] ?? "") && (code === "US" || code === "CA" || code === "AU" || code === "MX" || code === "BR" || code === "IN");
  const out = keys.map((key, i) => {
    const name = names[i] || key;
    const latin = lnames[i] ? latinClean(lnames[i]) : /^[\p{Script=Latin}\s\d.'’\-()]+$/u.test(name) ? name : key;
    const value = byCode ? key : latin;
    const label = byCode ? `${name} (${key})` : latin !== name ? `${latin} (${name})` : name;
    return { key, name, latin, iso: isos[i] ?? "", value, label };
  });
  subCache.set(code, out);
  return out;
}

/** A state or province written any way ("pennsylvania", "Pa.", "广东省", "Guangdong Province"): its stored value, or null. */
export function findRegion(cc: string, text: string): Subdivision | null {
  const k = keyOf(text);
  if (!k) return null;
  return subdivisions(cc).find((s) => [s.key, s.name, s.latin, s.iso, s.value].some((x) => x && keyOf(x) === k)) ?? null;
}

type City = { province: Subdivision; name: string; latin: string };
let cnCities: City[] | null = null;
/** China's cities with their provinces (from its data), so a city alone finds its province. */
function chinaCities(): City[] {
  if (cnCities) return cnCities;
  const subs = subdivisions("CN");
  const list: City[] = [];
  for (const [prov, c] of Object.entries(ADDRESS_DATA.CN?.cities ?? {})) {
    const province = subs.find((s) => s.key === prov);
    if (!province) continue;
    const names = c.names.split("~");
    const lnames = c.lnames.split("~");
    names.forEach((name, i) => {
      list.push({ province, name, latin: lnames[i] ? latinClean(lnames[i]) : name });
    });
  }
  cnCities = list;
  return list;
}
const findCnCity = (text: string, province?: Subdivision | null): City | null => {
  const k = keyOf(text);
  if (!k) return null;
  return chinaCities().find((c) => (!province || c.province.key === province.key) && (keyOf(c.name) === k || keyOf(c.latin) === k)) ?? null;
};

// Tidy and check --------------------------------------------------------------------------------------------------

const ALIASES: Record<string, string> = {
  usa: "US",
  unitedstatesofamerica: "US",
  america: "US",
  uk: "GB",
  greatbritain: "GB",
  england: "GB",
  scotland: "GB",
  wales: "GB",
  northernireland: "GB",
  prc: "CN",
  prchina: "CN",
  peoplesrepublicofchina: "CN",
  mainlandchina: "CN",
  中国: "CN",
  中华人民共和国: "CN",
  中國: "CN",
  台湾: "TW",
  台灣: "TW",
  roc: "TW",
  republicofchina: "TW",
  香港: "HK",
  hongkongsar: "HK",
  澳门: "MO",
  macau: "MO",
  日本: "JP",
  korea: "KR",
  southkorea: "KR",
  republicofkorea: "KR",
  한국: "KR",
  deutschland: "DE",
  holland: "NL",
  thenetherlands: "NL",
  uae: "AE",
  vietnam: "VN",
  russia: "RU",
};
const countryKey = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKC")
    .replace(/[\s.,'’\-()]/g, "");
const COUNTRY_BY_KEY = (() => {
  const m = new Map<string, string>();
  for (const { code, name } of COUNTRIES) {
    m.set(countryKey(name), code);
    if (ADDRESS_DATA[code]?.name) m.set(countryKey(ADDRESS_DATA[code].name as string), code);
  }
  for (const [k, v] of Object.entries(ALIASES)) m.set(k, v);
  return m;
})();
/** A country written any way ("U.S.A.", "China", "P.R. China", "中国", "gb"): its code, or null. */
export function findCountry(text: string): string | null {
  const t = text.trim();
  if (/^[A-Za-z]{2}$/.test(t) && isCountry(t)) return t.toUpperCase();
  return COUNTRY_BY_KEY.get(countryKey(t)) ?? null;
}

const zipRe = (cc: string) => {
  const z = rulesOf(cc).zip;
  return z ? new RegExp(`^(?:${z})$`, "i") : null;
};
/** A postal code as its country writes it: US 19103-1234, Canada K1A 0B1, the UK SW1A 2AA; else as typed, upper-cased. */
export function formatPostal(cc: string, text: string): string {
  const t = text.trim().toUpperCase().replace(/\s+/g, " ");
  const code = cc.toUpperCase();
  if (code === "US") {
    const m = t.replace(/\s/g, "").match(/^(\d{5})-?(\d{4})?$/);
    if (m) return m[2] ? `${m[1]}-${m[2]}` : m[1];
  }
  if (code === "CA" || code === "GB") {
    const flat = t.replace(/\s/g, "");
    if (flat.length >= 5 && flat.length <= 7) return `${flat.slice(0, -3)} ${flat.slice(-3)}`;
  }
  return t;
}

const tidy = (s: string) => s.replace(/\s+/g, " ").replace(/\s+,/g, ",").trim();
/** An address tidied: spaces, the country's code, its state or province by its proper name, a Chinese city in Latin, the
 * postal code as the country writes it. Nothing is invented: what it can't place stays as typed. */
export function normalizeAddress(a: Partial<Address>): Address {
  const out: Address = { ...EMPTY_ADDRESS };
  for (const k of ADDRESS_KEYS) out[k] = tidy(String(a[k] ?? ""));
  out.country = findCountry(out.country) ?? out.country.toUpperCase();
  const region = out.region ? findRegion(out.country, out.region) : null;
  if (region) out.region = region.value;
  if (out.country === "CN" && out.city) {
    const city = findCnCity(out.city, region);
    if (city) {
      out.city = city.latin;
      if (!out.region) out.region = city.province.value;
    }
  }
  if (out.postal_code) out.postal_code = formatPostal(out.country, out.postal_code);
  return out;
}

/** What's not right with an address yet, in words: no country, a field its country needs, a postal code or a state that
 * isn't one. An address with nothing in it has no problems (it's not given). `missing: false`: only what's wrong (what
 * a save refuses), not what's still to fill in. */
export function addressProblems(a: Address, o: { missing?: boolean } = {}): string[] {
  const missing = o.missing ?? true; // what isn't given yet (a form saving field by field asks only for what's wrong)
  if (ADDRESS_KEYS.every((k) => !a[k].trim())) return [];
  if (!a.country) return missing ? ["Pick its country"] : [];
  if (!isCountry(a.country)) return [`${a.country} isn't a country code`];
  const out: string[] = [];
  if (missing) for (const f of addressFields(a.country)) if (f.required && !a[f.key].trim()) out.push(`Its ${f.label.toLowerCase()} is missing`);
  const re = zipRe(a.country);
  const zip = addressFields(a.country).find((f) => f.key === "postal_code");
  if (a.postal_code && re && !re.test(a.postal_code))
    out.push(
      `${a.postal_code} isn't a ${(zip?.label ?? "postal code").toLowerCase()} in ${countryName(a.country)}${zip?.placeholder ? ` (like ${zip.placeholder})` : ""}`,
    );
  if (a.region && subdivisions(a.country).length && !findRegion(a.country, a.region))
    out.push(
      `${a.region} isn't a ${(addressFields(a.country).find((f) => f.key === "region")?.label ?? "province").toLowerCase()} of ${countryName(a.country)}`,
    );
  return out;
}

/** True when an address has nothing in it. */
export const isEmptyAddress = (a: Partial<Address> | null | undefined) => !a || ADDRESS_KEYS.every((k) => !String(a[k] ?? "").trim());

// Write ----------------------------------------------------------------------------------------------------------

/**
 * An address's lines as its country writes them, in Latin script (its `lfmt`) unless `local`. `name` and `org` go where
 * the country puts them; the country's name ends it unless it's `home` (HHL's: the US) or `country: false`.
 */
export function formatAddress(a: Address, o: { name?: string; org?: string; local?: boolean; home?: string; country?: boolean } = {}): string[] {
  if (isEmptyAddress(a)) return [o.org, o.name].filter((x): x is string => !!x);
  const r = rulesOf(a.country || "ZZ");
  const fmt = (o.local ? r.fmt : (r.lfmt ?? r.fmt)) ?? "%N%n%O%n%A%n%C";
  const region = a.region ? (findRegion(a.country, a.region) ?? null) : null;
  const regionText = region ? (o.local ? region.name : region.value) : a.region;
  const values: Record<string, string> = {
    N: o.name ?? "",
    O: o.org ?? "",
    A: [a.line1, a.line2].filter(Boolean).join("\n"),
    D: a.district,
    C: a.city,
    S: regionText,
    Z: a.postal_code,
  };
  const lines = fmt
    .replace(/%([NOADCSZ])/g, (_, f: string) => values[f] ?? "")
    .split("%n")
    .flatMap((l) => l.split("\n"))
    // What an empty field leaves behind: ", 12345", "City, ", double spaces
    .map((l) =>
      l
        .replace(/^[\s,]+|[\s,]+$/g, "")
        .replace(/,\s*,/g, ",")
        .replace(/\s{2,}/g, " "),
    )
    .filter(Boolean);
  const home = (o.home ?? "US").toUpperCase();
  if (o.country !== false && a.country && a.country.toUpperCase() !== home) lines.push(countryName(a.country));
  return lines;
}
/** One line ("12 Main St, Philadelphia, PA 19103, United States") for a list's cell. */
export const addressLine = (a: Address, o?: Parameters<typeof formatAddress>[1]) => formatAddress(a, { home: "", ...o }).join(", ");

// Read ------------------------------------------------------------------------------------------------------------

const CJK = /[㐀-鿿]/;
/** A part that's a unit within a building: a room, a floor, a suite or apartment, a short code like 3F or 1D. */
const UNIT =
  /^(room|rm|suite|ste|apt|apartment|unit|flat|floor|fl|#)\b|^\d+(st|nd|rd|th)\s+floor$|^\d{1,3}\s*f$|^\d{1,4}[a-z]?$|^[a-z]\d{1,4}$|^\d{1,3}[a-z]$|室$|楼$|层$/i;
/** A part that reads like a street, never a city: a number in it, or a word for a road. */
const STREETY =
  /\d|\b(road|rd|street|st|avenue|ave|lane|ln|boulevard|blvd|drive|dr|way|section|sec|alley|no|building|bldg|floor|fl|unit|suite|ste|room|rm|industrial|park|zone|village|town|lu|jie|dao|hao)\b\.?|[路街道号]/i;

/**
 * A whole address pasted from an email or a PI, split into its fields: the country by name (else `hint`, the vendor's
 * country), the postal code by the country's pattern, the state or province by its list, the city (China: by its list,
 * which also finds the province), a district (区, "District"), and the rest as the street lines. `left`: what it read
 * but couldn't place, for someone to check.
 */
export function parseAddress(text: string, hint?: string): { address: Address; left: string[] } {
  const raw = text
    .normalize("NFKC")
    .replace(/[，、；;]/g, ",")
    .replace(/\r/g, "")
    .trim();
  if (CJK.test(raw) && !/[a-z]{3,}/i.test(raw.replace(/[\d\s,.-]/g, "").slice(0, 40))) return parseChinese(raw, hint);
  let parts = raw
    .split(/\n|,/)
    .map((s) => tidy(s))
    .filter(Boolean);
  const a: Address = { ...EMPTY_ADDRESS };
  // The country: a part of its own (usually last), or a part's first or last words ("China 518101", "Shenzhen China")
  for (let i = parts.length - 1; i >= 0 && !a.country; i--) {
    const cc = findCountry(parts[i]);
    if (cc) {
      a.country = cc;
      parts.splice(i, 1);
      break;
    }
    const words = parts[i].split(/\s+/);
    for (let n = Math.min(4, words.length - 1); n >= 1 && !a.country; n--) {
      const tail = findCountry(words.slice(-n).join(" "));
      const head = tail ? null : findCountry(words.slice(0, n).join(" "));
      if (tail) {
        a.country = tail;
        parts[i] = words.slice(0, -n).join(" ");
      } else if (head) {
        a.country = head;
        parts[i] = words.slice(n).join(" ");
      }
    }
  }
  a.country ||= hint ? (findCountry(hint) ?? "") : "";
  const cc = a.country || "US";
  // The postal code: a word that's the country's pattern (looking from the end)
  const zip = rulesOf(cc).zip;
  if (zip) {
    const re = new RegExp(`(^|[\\s,])(${zip})(?=$|[\\s,])`, "i");
    for (let i = parts.length - 1; i >= 0 && !a.postal_code; i--) {
      const m = parts[i].match(re);
      if (m) {
        a.postal_code = formatPostal(cc, m[2]);
        parts[i] = tidy(parts[i].replace(m[2], ""));
      }
    }
    parts = parts.filter(Boolean);
  }
  // The state or province: a part that is one, or a part's last words ("Philadelphia PA", "Guangdong Province")
  for (let i = parts.length - 1; i >= 0 && !a.region; i--) {
    const whole = findRegion(cc, parts[i]);
    if (whole) {
      a.region = whole.value;
      parts.splice(i, 1);
      break;
    }
    const words = parts[i].split(/\s+/);
    for (let n = Math.min(3, words.length - 1); n >= 1 && !a.region; n--) {
      const found = findRegion(cc, words.slice(-n).join(" "));
      if (found) {
        a.region = found.value;
        parts[i] = words.slice(0, -n).join(" ");
      }
    }
  }
  // A district: a part that says so ("Bao'an District", "Nanshan Qu")
  const di = parts.findIndex((p) => /\b(district|dist\.?|qu)(\s|$)/i.test(p) && !/\d/.test(p));
  if (di >= 0) {
    a.district = parts[di];
    parts.splice(di, 1);
  }
  // The city: China's by its list (it also finds the province); elsewhere the part just before the state or postal code
  if (cc === "CN") {
    for (let i = parts.length - 1; i >= 0 && !a.city; i--) {
      const city = findCnCity(parts[i], a.region ? findRegion("CN", a.region) : null) ?? findCnCity(parts[i]);
      if (city) {
        a.city = city.latin;
        a.region ||= city.province.value;
        parts.splice(i, 1);
      }
    }
  } else if (parts.length > 1 || (parts.length === 1 && (a.region || a.postal_code))) {
    // Never a part that reads like a street: a number in it, or a word for a road ("Zhongshan Rd", "Section 2")
    const last = parts[parts.length - 1];
    if (!STREETY.test(last)) {
      a.city = last;
      parts.pop();
    }
  }
  // A country that writes its district where the city goes (Taiwan's 區: Xinzhuang Dist., New Taipei City)
  if (a.district && !(rulesOf(cc).lfmt ?? rulesOf(cc).fmt ?? "").includes("%D") && (!a.city || keyOf(a.city) === keyOf(a.region))) {
    a.city = a.district;
    a.district = "";
  }
  // The rest: the street on its first line, a unit (a room, a floor, a suite: "Room 301", "5th Floor", "3F", "1D") on its second
  const street = parts.filter(Boolean);
  const unit = (s: string) => UNIT.test(s);
  a.line1 = street.filter((s) => !unit(s)).join(", ") || (street[0] ?? "");
  a.line2 = street.filter((s) => unit(s) && s !== a.line1).join(", ");
  const out = normalizeAddress(a);
  const left = addressProblems(out);
  return { address: out, left };
}

/** 广东省深圳市宝安区西乡街道新和路5号 518101: province, city, district and the rest, by China's own lists. */
function parseChinese(raw: string, hint?: string): { address: Address; left: string[] } {
  let t = raw.replace(/\s+/g, "");
  const a: Address = { ...EMPTY_ADDRESS, country: "CN" };
  if (hint && findCountry(hint) && findCountry(hint) !== "CN") {
    const cc = findCountry(hint) as string;
    if (cc === "TW" || cc === "HK" || cc === "MO") a.country = cc;
  }
  t = t.replace(/^(中国|中國|中华人民共和国)/, "");
  const zip = t.match(/(?<!\d)(\d{6})(?!\d)/);
  if (zip && a.country === "CN") {
    a.postal_code = zip[1];
    t = t.replace(zip[1], "");
  }
  const subs = subdivisions(a.country);
  const prov = subs
    .flatMap((s) => [s.key, s.name].map((n) => ({ s, n })))
    .sort((x, y) => y.n.length - x.n.length)
    .find(({ n }) => n && t.startsWith(n));
  let province = prov?.s ?? null;
  if (prov) t = t.slice(prov.n.length);
  if (a.country === "CN") {
    const city = chinaCities()
      .filter((c) => !province || c.province.key === province.key)
      .flatMap((c) => [c.name, c.name.replace(/市$/, "")].map((n) => ({ c, n })))
      .sort((x, y) => y.n.length - x.n.length)
      .find(({ n }) => n && t.startsWith(n));
    if (city) {
      a.city = city.c.latin;
      province ??= city.c.province;
      t = t.slice(city.n.length).replace(/^市/, "");
    }
  }
  if (province) a.region = province.value;
  const district = t.match(/^(.{1,8}?(?:区|县|旗))/);
  if (district) {
    a.district = district[1];
    t = t.slice(district[1].length);
  }
  a.line1 = t.replace(/^[,\s]+|[,\s]+$/g, "");
  const out = normalizeAddress(a);
  return { address: out, left: addressProblems(out) };
}

// Locations -------------------------------------------------------------------------------------------------------

/** A location row's address columns (migration 077: `address_…`), and the one block of text it had before them. */
export type LocationAddressRow = {
  address?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  address_district?: string | null;
  address_city?: string | null;
  address_region?: string | null;
  address_postal_code?: string | null;
  address_country?: string | null;
};
/** A location's address in its fields. */
export const locationAddress = (l: LocationAddressRow): Address => ({
  line1: l.address_line1 ?? "",
  line2: l.address_line2 ?? "",
  district: l.address_district ?? "",
  city: l.address_city ?? "",
  region: l.address_region ?? "",
  postal_code: l.address_postal_code ?? "",
  country: l.address_country ?? "",
});
/** A location's address lines as its country writes them; until its fields are filled in, the text it had before. */
export function locationLines(l: LocationAddressRow, o?: Parameters<typeof formatAddress>[1]): string[] {
  const a = locationAddress(l);
  if (!isEmptyAddress(a)) return formatAddress(normalizeAddress(a), o);
  return (l.address ?? "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}
